import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, SegmentedControl, Tooltip } from '../ui';
import TrendViewV2 from './TrendViewV2';
import MarketViewV2 from './MarketViewV2';

export const WINDOW_TIP = 'Filter sold data to a specific date range. Affects KPIs, charts and variant trends.';

/** "Showing 2026-01-01 → today" (today's wording). */
export const windowText = (from, to) => `${from || '…'} → ${to || 'today'}`;

/**
 * The analysis window (P-BR-100..102): From / To dates, Apply reloads the
 * trends for that window (both empty: unfiltered), Clear (only when
 * filtered) empties it and reloads.
 */
function DateBar({ dateFrom, dateTo, onApply }) {
    const [from, setFrom] = useState(dateFrom || '');
    const [to, setTo] = useState(dateTo || '');
    useEffect(() => { setFrom(dateFrom || ''); setTo(dateTo || ''); }, [dateFrom, dateTo]);
    const filtered = Boolean(dateFrom || dateTo);
    return (
        <div className="br2-datebar" role="group" aria-label="Analysis window">
            <span className="br2-datebar-l">
                Analysis Window
                <Tooltip label="About the analysis window" content={WINDOW_TIP} />
            </span>
            <input type="date" className="v2-input br2-date" aria-label="From" value={from} onChange={(e) => setFrom(e.target.value)} />
            <span aria-hidden="true">→</span>
            <input type="date" className="v2-input br2-date" aria-label="To" value={to} onChange={(e) => setTo(e.target.value)} />
            <Button size="sm" onClick={() => onApply(from, to)}>Apply</Button>
            {filtered && (
                <button type="button" className="v2-clear" onClick={() => { setFrom(''); setTo(''); onApply('', ''); }}>Clear</button>
            )}
            {filtered && (
                <span className="v2-pill br2-window" data-testid="window-notice">
                    <span className="br2-pill-k">Showing</span> {windowText(dateFrom, dateTo)}
                </span>
            )}
        </div>
    );
}

DateBar.propTypes = {
    dateFrom: PropTypes.string, dateTo: PropTypes.string, onApply: PropTypes.func.isRequired,
};

/**
 * Category Trends tab: Trend (default) and Market sub-views over the same
 * /category-trends payload. The analysis window bar sits on Trend (as
 * today); on Market a note says which window the figures use (R10).
 */
export default function CategoryTrendsV2({ data }) {
    const [sub, setSub] = useState('trend');
    const {
        trendsData, trendsLoading, trendsError, retryTrends, trendDateFrom, trendDateTo, applyTrendWindow,
    } = data;
    const filtered = Boolean(trendDateFrom || trendDateTo);
    return (
        <div className="br2-panel">
            <div className="br2-subbar">
                <SegmentedControl
                    label="Category trends view"
                    value={sub}
                    onChange={setSub}
                    options={[{ value: 'trend', label: 'Trend' }, { value: 'market', label: 'Market' }]}
                />
                <span className="br2-spacer" />
                {sub === 'trend' && (
                    <DateBar dateFrom={trendDateFrom} dateTo={trendDateTo} onApply={applyTrendWindow} />
                )}
                {sub === 'market' && filtered && (
                    <span className="v2-pill br2-window" data-testid="market-window">
                        <span className="br2-pill-k">Window</span> {windowText(trendDateFrom, trendDateTo)}
                    </span>
                )}
            </div>
            {sub === 'trend' ? (
                <TrendViewV2 trendsData={trendsData} loading={trendsLoading} error={trendsError} onRetry={retryTrends} />
            ) : (
                <MarketViewV2 trendsData={trendsData} loading={trendsLoading} error={trendsError} onRetry={retryTrends} />
            )}
        </div>
    );
}

CategoryTrendsV2.propTypes = { data: PropTypes.object.isRequired };
